import { supabase } from '@/integrations/supabase/client';
import { scheduleAppointmentReminders } from './appointmentReminders';
import { 
  formatAppointmentDate, 
  formatAppointmentTime 
} from '@/lib/mailchimp-calendar';

export interface FormSubmissionData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  message?: string;
  source: 'contact_form' | 'demo_request' | 'quote_request' | 'home_internet';
  servicesInterested?: string[];
  serviceAnswers?: Record<string, any>;
  isDemoRequest?: boolean;
  demoDate?: string;
  demoTime?: string;
  leadSourceTag?: string;
}

export interface FormSubmissionResult {
  success: boolean;
  message: string;
  contactId?: string;
  error?: any;
}

export const submitForm = async (data: FormSubmissionData): Promise<FormSubmissionResult> => {
  console.log('Form submission started');
  
  try {
    // Use the secure edge function for form submission with proper validation
    const { data: result, error: submitError } = await supabase.functions.invoke('submit-form', {
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        postalCode: data.postalCode || null,
        message: data.message || null,
        source: data.source,
        servicesInterested: data.servicesInterested || [],
        serviceAnswers: data.serviceAnswers || null,
        demoDate: data.demoDate || null,
        demoTime: data.demoTime || null,
        isDemoRequest: data.isDemoRequest || false,
        leadSourceTag: data.leadSourceTag || null,
      }
    });

    if (submitError) {
      console.error('Form submission failed');
      return {
        success: false,
        message: 'Failed to submit form. Please try again or call us at (512) 859-5047.'
      };
    }
    
    const contact = { id: result.contactId };

    // Send confirmation email to customer via Fastmail SMTP (all form types)
    try {
      await supabase.functions.invoke('send-fastmail-confirmation', {
        body: {
          customerEmail: data.email,
          customerName: `${data.firstName} ${data.lastName}`,
          firstName: data.firstName,
          source: data.source,
          email: data.email,
          phone: data.phone || null,
          company: data.company || null,
          address: data.address || null,
          message: data.message || null,
          servicesInterested: data.servicesInterested || [],
          serviceAnswers: data.serviceAnswers || null,
          demoDate: data.demoDate || null,
          demoTime: data.demoTime || null,
          partnerApplication: (data as any).partnerApplication === true,
          leadSourceTag: data.leadSourceTag || null,
        }
      });
      console.log('✅ Confirmation email sent via Fastmail');
    } catch (emailError) {
      console.error('❌ Failed to send confirmation email:', emailError);
      // Don't fail the submission if email fails
    }

    // If this is a demo request with date/time, send internal notification and schedule reminders
    if ((data.source === 'demo_request' || data.isDemoRequest) && data.demoDate && data.demoTime && result.appointmentId) {
      // Schedule reminder emails (both customer and internal)
      await scheduleAppointmentReminders({
        appointmentId: result.appointmentId,
        contactId: contact.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || '',
        scheduledDate: data.demoDate,
        scheduledTime: data.demoTime,
      });
    }


    return {
      success: true,
      message: 'Thank you! We\'ll be in touch shortly.',
      contactId: contact.id
    };

  } catch (error) {
    console.error('Form submission error');
    
    return {
      success: false,
      message: 'There was an error submitting your form. Please try again or call us at (512) 859-5047.'
    };
  }
};
